import type { AppearanceDetail, AppearancePatchInput } from '../types';

export interface AppearanceFormValues {
  custom_title: string;
  model_color: string | null;
  model_pattern: string | null;
  model_finish: string | null;
}

export const EMPTY_APPEARANCE_FORM: AppearanceFormValues = {
  custom_title: '',
  model_color: null,
  model_pattern: null,
  model_finish: null,
};

export function formValuesFromDetail(detail: AppearanceDetail): AppearanceFormValues {
  return {
    custom_title: detail.custom_title ?? '',
    model_color: detail.model_color?.id ?? null,
    model_pattern: detail.model_pattern?.id ?? null,
    model_finish: detail.model_finish?.id ?? null,
  };
}

const blankToNull = (value: string) => value.trim() || null;

/** Builds a partial PATCH: only fields that differ from the initial values are sent. */
export function appearancePatch(
  initial: AppearanceFormValues,
  values: AppearanceFormValues
): AppearancePatchInput {
  const patch: AppearancePatchInput = {};
  if (values.model_color !== initial.model_color && values.model_color)
    patch.model_color = values.model_color;
  if (values.custom_title.trim() !== initial.custom_title)
    patch.custom_title = blankToNull(values.custom_title);
  if (values.model_pattern !== initial.model_pattern) patch.model_pattern = values.model_pattern;
  if (values.model_finish !== initial.model_finish) patch.model_finish = values.model_finish;
  return patch;
}

/** Live code preview `B{n}-D{n}-P{n}`; `B?` until a colour is selected. */
export function composeCodePreview(
  colorCode: string | null | undefined,
  patternCode: string | null | undefined,
  finishCode: string | null | undefined
) {
  return [colorCode || 'B?', patternCode || 'D0', finishCode || 'P0'].join('-');
}

/** Maps DRF field errors to form field names. */
export function appearanceFieldErrors(
  details: unknown
): Partial<Record<keyof AppearanceFormValues, string>> {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  const join = (value: unknown) => (Array.isArray(value) ? value.join(', ') : String(value));
  const source = details as Record<string, unknown>;
  const result: Partial<Record<keyof AppearanceFormValues, string>> = {};
  (['custom_title', 'model_color', 'model_pattern', 'model_finish'] as const).forEach((key) => {
    if (source[key]) result[key] = join(source[key]);
  });
  return result;
}

export function hexToRgb(hex: string | null | undefined) {
  return hex && /^#[\da-f]{6}$/i.test(hex)
    ? [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16)).join(' · ')
    : '—';
}
